import React from 'react';
import { Form, FormGroup, FormControl, ControlLabel, Row, Col } from 'react-bootstrap';
import { defineMessages, FormattedMessage, injectIntl, InjectedIntlProps } from 'react-intl';
import {
    TEST_STATUS_AUTO_MERGED,
    TEST_STATUS_FAILED,
    TEST_STATUS_FLAGGED_FOR_REVIEW,
    TEST_STATUS_PASSED,
    TEST_STATUS_PENDING,
} from '../../common/types/TestTypes';
import './SnapshotFilters.scss';

/**
 * Every testcase summary status (lastStatus) a reviewer can filter by. Values are the exact stored
 * keys; review-required stays distinct from failed. Run-only states (e.g. rejected) are not
 * testcase summary statuses and are intentionally not listed.
 */
const statusMessages = defineMessages({
    passed: { id: 'snapshotTesting.status.passed', defaultMessage: 'Passed' },
    autoMerged: { id: 'snapshotTesting.status.autoMerged', defaultMessage: 'Auto-merged' },
    flaggedForReview: { id: 'snapshotTesting.status.flaggedForReview', defaultMessage: 'Review required' },
    failed: { id: 'snapshotTesting.status.failed', defaultMessage: 'Failed' },
    pending: { id: 'snapshotTesting.status.pending', defaultMessage: 'Pending' },
});

export const SNAPSHOT_STATUS_FILTER_OPTIONS = [
    { value: TEST_STATUS_PASSED, message: statusMessages.passed },
    { value: TEST_STATUS_AUTO_MERGED, message: statusMessages.autoMerged },
    { value: TEST_STATUS_FLAGGED_FOR_REVIEW, message: statusMessages.flaggedForReview },
    { value: TEST_STATUS_FAILED, message: statusMessages.failed },
    { value: TEST_STATUS_PENDING, message: statusMessages.pending },
];

export interface SnapshotFiltersState {
    search: string;
    status: string;
    sortBy: string;
}

interface Props {
    filters: SnapshotFiltersState;
    onFilterChange: (filters: SnapshotFiltersState) => void;
}

export class SnapshotFilters extends React.Component<Props & InjectedIntlProps> {
    handleChange = (name: keyof SnapshotFiltersState, value: string) => {
        this.props.onFilterChange({
            ...this.props.filters,
            [name]: value
        });
    };

    render() {
        const { filters, intl } = this.props;

        return (
            <Form className="snapshot-filters">
                <Row>
                    <Col md={4}>
                        <FormGroup controlId="snapshotFilterSearch">
                            <ControlLabel>
                                <FormattedMessage defaultMessage="Search" id="snapshotTesting.filters.search" />
                            </ControlLabel>
                            <FormControl
                                onChange={(e: React.FormEvent<FormControl>) => this.handleChange('search', (e.target as HTMLInputElement).value)}
                                placeholder={intl.formatMessage({ id: 'snapshotTesting.filters.searchPlaceholder', defaultMessage: 'Search by name...' })}
                                type="text"
                                value={filters.search}
                            />
                        </FormGroup>
                    </Col>
                    <Col md={4}>
                        <FormGroup controlId="snapshotFilterStatus">
                            <ControlLabel>
                                <FormattedMessage defaultMessage="Status" id="snapshotTesting.filters.status" />
                            </ControlLabel>
                            <FormControl
                                componentClass="select"
                                onChange={(e: React.FormEvent<FormControl>) => this.handleChange('status', (e.target as HTMLInputElement).value)}
                                value={filters.status}
                            >
                                <option value="">{intl.formatMessage({ id: 'snapshotTesting.filters.allStatuses', defaultMessage: 'All Statuses' })}</option>
                                {SNAPSHOT_STATUS_FILTER_OPTIONS.map(option => (
                                    <option key={option.value} value={option.value}>{intl.formatMessage(option.message)}</option>
                                ))}
                            </FormControl>
                        </FormGroup>
                    </Col>
                    <Col md={4}>
                        <FormGroup controlId="snapshotFilterSort">
                            <ControlLabel>
                                <FormattedMessage defaultMessage="Sort By" id="snapshotTesting.filters.sortBy" />
                            </ControlLabel>
                            <FormControl
                                componentClass="select"
                                onChange={(e: React.FormEvent<FormControl>) => this.handleChange('sortBy', (e.target as HTMLInputElement).value)}
                                value={filters.sortBy}
                            >
                                <option value="name">{intl.formatMessage({ id: 'snapshotTesting.filters.sortName', defaultMessage: 'Name' })}</option>
                                <option value="updatedAt">{intl.formatMessage({ id: 'snapshotTesting.filters.sortLastUpdated', defaultMessage: 'Last Updated' })}</option>
                            </FormControl>
                        </FormGroup>
                    </Col>
                </Row>
            </Form>
        );
    }
}

export default injectIntl(SnapshotFilters);
